// @ts-strict-ignore
import { DateTime } from "@dashboard/components/Date";
import Money from "@dashboard/components/Money";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { GetVendorQuotesQuery, useQuotationSendToEvolveMutation } from "@dashboard/graphql";
import { maybe, renderCollection } from "@dashboard/misc";
import { QuotationListUrlSortField, quotationUrl } from "@dashboard/quotations/urls";
import { ListProps, RelayToFlat, SortPage } from "@dashboard/types";
import { getArrowDirection } from "@dashboard/utils/sort";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { CSSProperties } from "@material-ui/styles";
import { makeStyles } from "@saleor/macaw-ui";
import { Skeleton } from "@saleor/macaw-ui-next";
import { Visibility as VisibilityIcon } from "@material-ui/icons";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useNotifier from "@dashboard/hooks/useNotifier";
import { SendIcon } from "lucide-react";
import { useUser } from "@dashboard/auth";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    return {
      [theme.breakpoints.up("lg")]: {
        colDate: {
          width: 100,
        },
        colProductName: {
          width: 100,
        },
        colTotal: {
          width: 100,
        },
        colAction: {
          width: 100,
        },
      },
      colDate: {
        padding: theme.spacing(0, 3),
      },
      colProductName: overflowing,
      colTotal: {
        textAlign: "right",
      },
      colAction: {
        textAlign: "left",
      },
      link: {
        cursor: "pointer",
      },
    };
  },
  { name: "QuotationList" },
);

interface QuotationListProps extends ListProps, SortPage<QuotationListUrlSortField> {
  quotations: RelayToFlat<GetVendorQuotesQuery["quotations"]>;
  onView: (token?: string | null) => void;
}

const numberOfColumns = 4;

export const QuotationList = (props: QuotationListProps) => {
  const { disabled, settings, quotations, onSort, sort, onView } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const notify = useNotifier();
  const { user } = useUser();
  const [quotationSendToEvolve, { loading: sendingToEvolve }] = useQuotationSendToEvolveMutation();

  const getProductNames = (quotation: any) => {
    if (!quotation?.checkout?.lines) {
      return "-";
    }

    const names = quotation.checkout.lines.map((line: any) => {
      // Pick metadata productName if exists
      const metaProductName = line?.metadata?.find(
        (item: any) => item.key === "productName",
      )?.value;

      if (metaProductName) return metaProductName;

      // fallback
      return line?.variant?.product?.name || line?.variant?.name || "-";
    });

    // Remove duplicates & falsy
    const uniqueNames = Array.from(new Set(names.filter(Boolean)));

    return uniqueNames.length > 0 ? uniqueNames.join(", ") : "-";
  };
  
  const handleSendToEvolve = async (quotationId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const { data } = await quotationSendToEvolve({
        variables: { id: quotationId },
      });

      const errors = data?.quotationSendToEvolve?.errors ?? [];
      if (errors.length > 0) {
        notify({
          status: "error",
          text:
            errors[0].message ||
            intl.formatMessage({
              id: "quotationSendToEvolveError",
              defaultMessage: "Failed to send quotation to Evolve.",
            }),
        });
        return;
      }

      notify({
        status: "success",
        text: intl.formatMessage({
          id: "quotationSendToEvolveSuccess",
          defaultMessage: "Quotation sent to Evolve successfully.",
        }),
      });
    } catch (error) {
      notify({
        status: "error",
        text: intl.formatMessage({
          id: "quotationSendToEvolveError",
          defaultMessage: "Failed to send quotation to Evolve.",
        }),
      });
    }
  };

  return (
    <ResponsiveTable>
      <TableHead>
        <TableRowLink>
          <TableCellHeader
            direction={
              sort.sort === QuotationListUrlSortField.date ? getArrowDirection(sort.asc) : undefined
            }
            onClick={() => onSort(QuotationListUrlSortField.date)}
            className={classes.colDate}
          >
            <FormattedMessage
              id="PHUcrU"
              defaultMessage="Date"
              description="date when quotation was requested"
            />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === QuotationListUrlSortField.productName
                ? getArrowDirection(sort.asc)
                : undefined
            }
            onClick={() => onSort(QuotationListUrlSortField.productName)}
            className={classes.colProductName}
          >
            <FormattedMessage
              id="productName"
              defaultMessage="Product Name"
              description="product name"
            />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === QuotationListUrlSortField.total
                ? getArrowDirection(sort.asc)
                : undefined
            }
            onClick={() => onSort(QuotationListUrlSortField.total)}
            textAlign="right"
            className={classes.colTotal}
          >
            <FormattedMessage id="k9hf7F" defaultMessage="Total" description="total price" />
          </TableCellHeader>
          <TableCellHeader className={classes.colAction}>
            <FormattedMessage id="action" defaultMessage="Action" description="action column" />
          </TableCellHeader>
        </TableRowLink>
      </TableHead>
      <TableFooter>
        <TableRowLink>
          <TablePaginationWithContext
            colSpan={numberOfColumns}
            settings={settings}
            disabled={disabled}
            onUpdateListSettings={props.onUpdateListSettings}
          />
        </TableRowLink>
      </TableFooter>
      <TableBody>
        {renderCollection(
          quotations,
          quotation => (
            <TableRowLink
              data-test-id="quotation-table-row"
              hover={!!quotation}
              key={quotation ? quotation.id : "skeleton"}
            >
              <TableCell className={classes.colDate}>
                {maybe(() => quotation.requestedAt) ? (
                  <DateTime date={quotation.requestedAt} plain />
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell className={classes.colProductName}>
                {quotation ? <span>{getProductNames(quotation)}</span> : <Skeleton />}
              </TableCell>
              <TableCell className={classes.colTotal} align="right">
                {maybe(() => quotation.checkout?.totalPrice?.net) ? (
                  <Money money={quotation.checkout.totalPrice.net} />
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell className={classes.colAction} align="center">
                {quotation ? (
                  <div className={classes.actionButtons}>
                    <Tooltip title="View Quotation" arrow placement="bottom">
                      <IconButton
                        disabled={!quotation.checkout?.token}
                        onClick={e => {
                          e.stopPropagation();
                          onView(quotation.checkout?.token);
                        }}
                        size="small"
                        data-test-id="view-quotation-button"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {user?.isVendor && (
                      <Tooltip title="Send Query to Evolve" arrow placement="bottom">
                        <IconButton
                          size="small"
                          onClick={e => handleSendToEvolve(quotation.id, e)}
                          disabled={sendingToEvolve || !quotation.id}
                          data-test-id="send-to-evolve-button"
                          style={{ marginLeft: "10px" }}
                        >
                          <SendIcon width={20} height={20} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
            </TableRowLink>
          ),
          () => (
            <TableRowLink>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage id="noQuotations" defaultMessage="No quotations found" />
              </TableCell>
            </TableRowLink>
          ),
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

QuotationList.displayName = "QuotationList";
export default QuotationList;
